import { ChevronDown, Heart, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  TERRITORIES_API_URL,
  TERRITORIES_LIKES_API_URL,
  TERRITORIES_REQUESTS_API_URL,
} from '@/constants/apis';
import { configuration } from '@/constants/config';
import { DEFAULT_CENSUS_DATA_FIELDS } from '@/constants/territories/variables';
import { apiManager } from '@/helpers/api.manager';
import { toast } from '@/hooks/ui/use-toast';
import { formatDecimal, formatValue } from '@/lib/string';
import { getTerritoryColor } from '@/lib/territories';
import { isDecimal } from '@/lib/territories';
import { useTerritoryStore } from '@/stores/useTerritoryStore';
import { useZorTerritorySettingsStore } from '@/stores/useZorTerritorySettingsStore';
import { CensusFieldKey } from '@/types/territories/census.field.type';
import { Territory } from '@/types/territories/territory.type';

import { TerritoryReserveDialog } from '../territory-item/TerritoryReserveDialog';

interface TerritoryStatsDialogProps {
  territory: Territory;
  onCloseDialog: () => void;
}

const TerritoryStatsDialog = ({
  territory,
  onCloseDialog,
}: TerritoryStatsDialogProps) => {
  if (!territory) return;
  const { territories, mapTerritories, setTerritories, setMapTerritories } =
    useTerritoryStore();

  const [confirmReserveDialogOpen, setConfirmReserveDialogOpen] =
    useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRequestReserveLoading, setIsRequestReserveLoading] =
    useState<boolean>(false);
  const [isZipCodesPopoverOpen, setIsZipCodesPopoverOpen] = useState(false);
  const { zorTerritorySettings } = useZorTerritorySettingsStore();
  const [demographic, setDemographic] = useState<Territory | null>(null);
  const [loadingTerritory, setLoadingTerritory] = useState(true);
  const soldTerritory = territory.status == 'SOLD';

  const maxFirstLineZips = 2;
  const firstLineZips = territory.zcta_codes.slice(0, maxFirstLineZips);
  const hasMoreZips = territory.zcta_codes.length > maxFirstLineZips;

  useEffect(() => {
    const fetchDemographic = async () => {
      const response = await apiManager.get(
        `${TERRITORIES_API_URL}/${territory.id}`,
        {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.status === 200) {
        setDemographic(response.data.data);
      } else {
        toast({
          title: 'Error loading demographic information',
          variant: 'destructive',
        });
      }

      setLoadingTerritory(false);
    };

    fetchDemographic();
  }, [territory.id]);

  const handleLikeTerritory = async (): Promise<void> => {
    setIsLoading(true);
    if (territory.is_liked) {
      const response = await apiManager.delete(
        `${TERRITORIES_LIKES_API_URL}/${territory.id}`,
        {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setIsLoading(false);
      if (response.status !== 200) {
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
        });
        return;
      }

      setTerritories(
        territories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: false, likes_count: t.likes_count - 1 }
            : t
        ) || []
      );

      setMapTerritories(
        mapTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: false, likes_count: t.likes_count - 1 }
            : t
        ) || []
      );

      toast({
        description: 'Territory unliked successfully',
      });
    } else {
      const response = await apiManager.post(
        `${TERRITORIES_LIKES_API_URL}/${territory.id}`,
        undefined,
        {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setIsLoading(false);
      if (response.status !== 200) {
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
        });
        return;
      }

      setTerritories(
        territories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: true, likes_count: t.likes_count + 1 }
            : t
        ) || []
      );

      setMapTerritories(
        mapTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: true, likes_count: t.likes_count + 1 }
            : t
        ) || []
      );

      toast({
        description: 'Territory liked successfully',
      });
    }
  };

  const handleRequestReserve = async (): Promise<void> => {
    setIsRequestReserveLoading(true);
    if (territory.request_status) {
      const response = await apiManager.delete(
        `${TERRITORIES_REQUESTS_API_URL}/${territory.id}`,
        {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setIsRequestReserveLoading(false);
      if (response.status !== 200) {
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
        });
        return;
      }

      setTerritories(
        territories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: null, status: 'AVAILABLE' }
            : t
        ) || []
      );

      setMapTerritories(
        mapTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: null, status: 'AVAILABLE' }
            : t
        ) || []
      );

      toast({
        description: 'Request canceled successfully',
      });
    } else {
      const response = await apiManager.post(
        `${TERRITORIES_REQUESTS_API_URL}/${territory.id}`,
        undefined,
        {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setIsRequestReserveLoading(false);
      if (response.status !== 200) {
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
        });
        return;
      }

      setTerritories(
        territories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: 'REQUESTED', status: 'PENDING' }
            : t
        ) || []
      );

      setMapTerritories(
        mapTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: 'REQUESTED', status: 'PENDING' }
            : t
        ) || []
      );

      toast({
        description: 'Request sent successfully',
      });
    }
    setConfirmReserveDialogOpen(false);
  };

  return (
    <div className="absolute left-1/2 top-1/2 flex max-h-[calc(100%-40px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-lg border border-border bg-white p-4 shadow-lg max-md:max-w-[calc(100%-40px)] md:max-w-md">
      <div className="flex items-center justify-between gap-5">
        <div className="flex flex-col gap-2 overflow-hidden">
          <p className="as-title-01 text-gray-700">
            {territory.name ||
              `Territory ${territory.id.toString().padStart(5, '0')}`}
          </p>
          <p className="as-body-03 text-muted-foreground">
            #{territory.id.toString().padStart(5, '0')}
          </p>
          <div className="flex items-center gap-1">
            <p className="as-body-03 as-strong truncate text-muted-foreground">
              Zip: {firstLineZips.join(', ')}
              {hasMoreZips && '...'}
            </p>
            {hasMoreZips && (
              <Popover
                open={isZipCodesPopoverOpen}
                onOpenChange={setIsZipCodesPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <button className="flex items-center justify-center rounded p-0.5 hover:bg-gray-100">
                    <ChevronDown className="h-3 w-3 text-gray-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4">
                  <div className="space-y-2">
                    <h4 className="as-body-02 as-strong text-gray-700">
                      All Zip Codes
                    </h4>
                    <p className="as-body-03 break-words text-gray-600">
                      {territory.zcta_codes.join(', ')}
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
        <Button
          size="icon"
          variant="outline"
          className="aspect-square"
          onClick={onCloseDialog}
        >
          <X />
        </Button>
      </div>
      <div className="flex gap-8">
        <div className="space-y-1">
          <p className="as-title-04 text-muted-foreground">Status</p>
          <Badge color={getTerritoryColor(territory, zorTerritorySettings)}>
            {territory.is_liked && territory.status == 'AVAILABLE'
              ? 'LIKED'
              : territory.status}
          </Badge>
        </div>
      </div>
      {!soldTerritory && (
        <div
          className={`overflow-y-auto transition-all duration-500 ease-in-out ${
            loadingTerritory ? 'max-h-20' : 'max-h-[1000px]'
          }`}
        >
          {loadingTerritory ? (
            <div className="flex justify-center gap-3 rounded-lg border border-border bg-muted p-4">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            demographic && (
              <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted p-4">
                {DEFAULT_CENSUS_DATA_FIELDS.map(({ id, label }) => (
                  <div
                    key={id}
                    className="flex items-center justify-between gap-5"
                  >
                    <p className="as-title-04 text-muted-foreground">{label}</p>
                    <p className="as-title-04 text-gray-700">
                      {id.includes('age') && id.includes('median')
                        ? formatDecimal(demographic?.[id as CensusFieldKey])
                        : formatValue(demographic?.[id as CensusFieldKey])}
                    </p>
                  </div>
                ))}
                {demographic.extra_census_data &&
                  demographic.extra_census_data.length > 0 &&
                  demographic.extra_census_data.map(
                    (item, index) =>
                      item.label && (
                        <div
                          key={index}
                          className="flex items-center justify-between gap-5"
                        >
                          <p className="as-title-04 text-muted-foreground">
                            {item.label}
                          </p>
                          <p className="as-title-04 text-gray-700">
                            {isDecimal(item.code)
                              ? formatDecimal(item.value)
                              : formatValue(item.value)}
                          </p>
                        </div>
                      )
                  )}
                {demographic.grouped_census_data &&
                  demographic.grouped_census_data.length > 0 && (
                    <>
                      {demographic.grouped_census_data.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between gap-5"
                        >
                          <p className="as-title-04 text-muted-foreground">
                            {item.label}
                          </p>
                          <p className="as-title-04 text-gray-700">
                            {formatValue(item.value)}
                          </p>
                        </div>
                      ))}
                    </>
                  )}
              </div>
            )
          )}
        </div>
      )}
      {!soldTerritory && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            disabled={isLoading || loadingTerritory}
            onClick={handleLikeTerritory}
          >
            {isLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Heart
                fill={territory.is_liked ? 'currentColor' : 'none'}
                className="size-4 text-red-500"
                stroke="black"
              />
            )}
            {territory.likes_count} Liked
          </Button>
          {territory.status === 'AVAILABLE' ? (
            <TerritoryReserveDialog
              isOpen={confirmReserveDialogOpen}
              onOpenChange={setConfirmReserveDialogOpen}
              onConfirm={handleRequestReserve}
              loading={isRequestReserveLoading || loadingTerritory}
            />
          ) : null}
        </div>
      )}
      {soldTerritory && (
        <p className="as-body-03 text-muted-foreground">
          This territory has already been sold.
        </p>
      )}
    </div>
  );
};

export { TerritoryStatsDialog };
